import React, { useEffect, useState } from "react";
import { GPDP_LOGO, GPDP_LOGO_ALT, APP_NAME, APP_TAGLINE } from "@/lib/constants";

/**
 * Full-screen branded splash overlay.
 *
 * - Shown only ONCE per browser session (sessionStorage flag).
 * - Auto-dismisses after `duration` ms with a fade-out.
 * - When launched as PWA (display-mode standalone), the splash acts as the
 *   Android "in-app splash" — matches manifest.json theme_color for zero flash.
 */
export default function SplashScreen({ duration = 1500 }) {
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    return !sessionStorage.getItem("simpel_splash_seen");
  });
  const [fadingOut, setFadingOut] = useState(false);

  useEffect(() => {
    if (!visible) return;
    const fadeAt = setTimeout(() => setFadingOut(true), duration);
    const hideAt = setTimeout(() => {
      setVisible(false);
      try { sessionStorage.setItem("simpel_splash_seen", "1"); } catch (_) {}
    }, duration + 500);
    return () => { clearTimeout(fadeAt); clearTimeout(hideAt); };
  }, [visible, duration]);

  if (!visible) return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center transition-opacity duration-500 ${fadingOut ? "opacity-0" : "opacity-100"}`}
      style={{ background: "linear-gradient(135deg, #0F2540 0%, #17365D 55%, #276AA3 100%)" }}
      data-testid="splash-screen"
      aria-hidden="true"
    >
      {/* Papua motif overlay */}
      <div className="absolute inset-0 papua-motif opacity-25" />

      {/* Radial glow */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="h-[520px] w-[520px] rounded-full bg-[#E5B52A]/25 blur-[120px] animate-pulse" />
      </div>

      <div className="relative flex flex-col items-center gap-6 px-6 text-center">
        {/* Rotating dashed ring */}
        <div className="relative h-40 w-40 flex items-center justify-center">
          <div className="absolute inset-0 rounded-full border-2 border-[#E5B52A]/60 animate-[spin_10s_linear_infinite]" style={{ borderStyle: "dashed" }} />
          <div className="absolute inset-4 rounded-full border border-white/25" />
          {/* Logo pedestal */}
          <div className="relative h-32 w-32 rounded-full bg-white shadow-[0_20px_60px_rgba(0,0,0,0.45)] flex items-center justify-center splash-pop">
            <img src={GPDP_LOGO} alt={GPDP_LOGO_ALT} className="h-24 w-24 object-contain" />
          </div>
        </div>

        {/* Brand text */}
        <div className="splash-fade-up">
          <div className="text-[0.65rem] tracking-[0.28em] uppercase text-[#E5B52A] font-bold">Klasis Mimika • Papua Tengah</div>
          <div className="mt-2 font-heading font-black text-3xl sm:text-4xl text-white leading-tight">{APP_NAME}</div>
          <div className="mt-3 text-xs sm:text-sm text-white/70 italic max-w-sm">"{APP_TAGLINE}"</div>
        </div>

        {/* Loading indicator */}
        <div className="mt-2 flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-[#E5B52A] animate-[bounce_1s_infinite]" />
          <span className="h-1.5 w-1.5 rounded-full bg-[#E5B52A] animate-[bounce_1s_infinite]" style={{ animationDelay: "150ms" }} />
          <span className="h-1.5 w-1.5 rounded-full bg-[#E5B52A] animate-[bounce_1s_infinite]" style={{ animationDelay: "300ms" }} />
        </div>
      </div>

      {/* Footer */}
      <div className="absolute bottom-6 left-0 right-0 text-center text-[0.65rem] tracking-widest uppercase text-white/50 splash-fade-up">
        GPDP Klasis Mimika • Sistem Manajemen Pelayanan
      </div>
    </div>
  );
}
